import { Organization } from '../../types/user';

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface PasswordRequirement {
  id: string;
  label: string;
  required: boolean;
  met: boolean;
}

export interface PasswordPolicyEvaluation extends PasswordValidationResult {
  requirements: PasswordRequirement[];
}

const resolvePolicies = (organization?: Organization | null) => ({
  requireUppercase: organization?.RequireUppercase ?? true,
  requireLowercase: organization?.RequireLowercase ?? true,
  requireNumbers: organization?.RequireNumbers ?? true,
  requireSpecialCharacters: organization?.RequireSpecialCharacters ?? true,
  minimumPasswordLength: organization?.MinimumPasswordLength ?? 8,
});

const getRequirementError = (
  id: string,
  policies: ReturnType<typeof resolvePolicies>
): string => {
  switch (id) {
    case 'minLength':
      return `Password must be at least ${policies.minimumPasswordLength} characters long`;
    case 'uppercase':
      return 'Password must contain at least one uppercase letter';
    case 'lowercase':
      return 'Password must contain at least one lowercase letter';
    case 'numbers':
      return 'Password must contain at least one number';
    case 'specialCharacters':
      return 'Password must contain at least one special character';
    default:
      return 'Password does not meet the requirements.';
  }
};

const buildRequirements = (
  password: string,
  policies: ReturnType<typeof resolvePolicies>
): PasswordRequirement[] => {
  const requirements: PasswordRequirement[] = [
    {
      id: 'minLength',
      label: `${policies.minimumPasswordLength}+ characters`,
      required: true,
      met: password.length >= policies.minimumPasswordLength,
    },
  ];

  if (policies.requireUppercase) {
    requirements.push({
      id: 'uppercase',
      label: 'Uppercase letter (A-Z)',
      required: true,
      met: /[A-Z]/.test(password),
    });
  }

  if (policies.requireLowercase) {
    requirements.push({
      id: 'lowercase',
      label: 'Lowercase letter (a-z)',
      required: true,
      met: /[a-z]/.test(password),
    });
  }

  if (policies.requireNumbers) {
    requirements.push({
      id: 'numbers',
      label: 'Number (0-9)',
      required: true,
      met: /[0-9]/.test(password),
    });
  }

  if (policies.requireSpecialCharacters) {
    requirements.push({
      id: 'specialCharacters',
      label: 'Special character (e.g. !@#$%)',
      required: true,
      met: /[\]!"#$%&'()*+,./:;<=>?@[\\^_`{|}~-]/.test(password),
    });
  }

  return requirements;
};

export const getPasswordRequirements = (
  password: string,
  organization?: Organization | null
): PasswordRequirement[] => buildRequirements(password, resolvePolicies(organization));

export const evaluatePasswordPolicies = (
  password: string,
  organization?: Organization | null
): PasswordPolicyEvaluation => {
  const policies = resolvePolicies(organization);
  const requirements = buildRequirements(password, policies);
  const errors = requirements
    .filter(requirement => !requirement.met)
    .map(requirement => getRequirementError(requirement.id, policies));

  return {
    requirements,
    isValid: errors.length === 0,
    errors,
  };
};
